import { registerBrick, BrickContext } from 'olympe';
import { ReactBrick, useProperty } from '@olympeio/core';
import { Typography, Button, ThemeProvider, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { EditableElementsRows } from './elements/EditableElementsRows.jsx';
import React from 'react';
import { globalTheme } from '../shared/_theme.jsx';
import { DESCRIPTION, FROM, TO, DESC_MAP, TEMPLATE } from './elements/_constants';
import './blocked_parked.css';

export default class ElementsForResponsability extends ReactBrick {
    /**
     * @override
     * @protected
     * @param {!BrickContext} $
     * @param {!Array<*>} properties
     * @returns {Element}
     */

    static getReactComponent($) {
        return (props) => {
            // Get input props
            const data = useProperty($, 'data');
            const rule = useProperty($, 'Rule');
            const editionEnabled = useProperty($, 'editionEnabled');
            const [editedData, setEditedData] = React.useState([].concat(data ?? []));

            React.useEffect(() => {
                setEditedData([].concat(data && data.length ? data : TEMPLATE));
            }, [data]);

            // handle data changes
            React.useEffect(() => {
                $.set('hasDataChanged', false);
            });
            React.useEffect(() => {
                $.set('editedData', editedData);
                $.set('hasDataChanged', true);
            }, [editedData]);

            // edit element handler
            const onEditElement = (event, index, key) => {
                const editedElement = Object.assign({}, editedData[index]);
                editedElement[key] = event.target.value;
                const nextData = [].concat(editedData);
                nextData.splice(index, 1, editedElement);
                setEditedData(nextData);
            };

            // duplicate element handler
            const onDuplicateElement = (index) => {
                const newElement = Object.assign({}, editedData[index]);
                newElement[FROM] = '';
                newElement[TO] = '';
                const nextData = [].concat(editedData);
                nextData.splice(index + 1, 0, newElement);
                setEditedData(nextData);
            };

            // remove element handler
            const onRemoveElement = (index) => {
                const nextData = [].concat(editedData);
                nextData.splice(index, 1);
                setEditedData(nextData);
            };

            // Buttons handlers
            const handleClose = () => {
                $.trigger('On Close');
            };
            const handleSave = () => {
                $.trigger('On Save');
            };

            // handle auto-height for non-editable version
            const wrapper = React.useRef();
            const resizeObserver = new ResizeObserver((entries) => {
                const entry = entries.at(0);
                $.set('Height', entry.contentRect.height);
            });
            React.useEffect(() => {
                resizeObserver.observe(wrapper.current);
                return () => wrapper.current && resizeObserver.unobserve(wrapper.current);
            }, []);
            React.useEffect(() => {
                updateHeight();
            }, [data]);
            let updateHeightTimeout;
            const updateHeight = () => {
                clearTimeout(updateHeightTimeout);
                updateHeightTimeout = setTimeout(() => {
                    !editionEnabled && wrapper.current && $.set('Height', wrapper.current.offsetHeight);
                }, 150);
            };

            const ContentWrapper = editionEnabled ? DialogContent : React.Fragment;

            return (
                <ThemeProvider theme={globalTheme}>
                    <div
                        className={`responsibility-elements ${
                            editionEnabled ? 'responsibility-elements-editable' : ''
                        }`}
                        ref={wrapper}
                    >
                        {editionEnabled ? <DialogTitle>Edit Responsibility Elements</DialogTitle> : undefined}
                        <ContentWrapper className="show-scrollbar">
                            <div className="responsibility-elements-grid">
                                <div className="responsibility-elements-headers">
                                    <Typography>Element description</Typography>
                                    <Typography>From</Typography>
                                    <Typography>To</Typography>
                                    <div></div>
                                </div>
                                {editionEnabled ? (
                                    <EditableElementsRows
                                        data={editedData}
                                        onEditElement={onEditElement}
                                        onDuplicateElement={onDuplicateElement}
                                        onRemoveElement={onRemoveElement}
                                        rule={rule}
                                    ></EditableElementsRows>
                                ) : (
                                    ElementsForResponsability.getNonEditableDataRows(data)
                                )}
                            </div>
                        </ContentWrapper>
                        {editionEnabled ? (
                            <DialogActions>
                                <Button variant="outlined" color="primary" onClick={handleClose}>
                                    Discard
                                </Button>
                                <Button variant="contained" color="primary" onClick={handleSave}>
                                    Save
                                </Button>
                            </DialogActions>
                        ) : undefined}
                    </div>
                </ThemeProvider>
            );
        };
    }

    /**
     *
     * @param {Array<*>} data
     * @returns {React.ReactElement}
     */
    static getNonEditableDataRows(data) {
        return (
            <>
                {Array.isArray(data) ? (
                    data.map((element, index) => (
                        <div className="responsibility-elements-row" key={index}>
                            <Typography>{DESC_MAP.get(element[DESCRIPTION])}</Typography>
                            <Typography>{element[FROM]}</Typography>
                            <Typography>{element[TO]}</Typography>
                            <div></div>
                        </div>
                    ))
                ) : (
                    <Typography>Loading ...</Typography>
                )}
            </>
        );
    }
}

registerBrick('018af4394cec2b2fe78e', ElementsForResponsability);
